/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */

// a CSV column - the row key it reads and its header label ...
export interface CsvColumn {
  key: string;
  label: string;
}

export type CsvRow = Record<string, string>;

// a leading formula-injection character (`=`, `+`, `-`, `@`, a tab or a
// carriage return) that a spreadsheet app would otherwise interpret as a
// formula ...
const CSV_FORMULA_PREFIX = /^[=+\-@\t\r]/;

// a value needing quotes - one holding a comma, quote or line break ...
const CSV_QUOTED_VALUE = /[",\r\n]/;

// the byte order mark that tells spreadsheet apps the file is UTF-8 ...
const UTF8_BOM = '\uFEFF';

// a CSV field, quoted when it holds a comma, quote or line break; a leading
// formula-injection character is neutralised with a leading single quote,
// which spreadsheet apps strip from a cell's displayed value, before that
// quoting ...
const toCsvField = (value: string) => {
  const safeValue = CSV_FORMULA_PREFIX.test(value) ? `'${value}` : value;
  return CSV_QUOTED_VALUE.test(safeValue) ? `"${safeValue.replace(/"/g, '""')}"` : safeValue;
};

const toCsvLine = (fields: string[]) => fields.map(toCsvField).join(',');

/**
 * Builds CSV text from a table's columns and rows - a header line of the
 * column labels, then a line per row, separated by CRLF. A field holding a
 * comma, quote or line break is quoted, and a field starting with a
 * formula-injection character (`=`, `+`, `-`, `@`, tab or carriage return)
 * is prefixed with a single quote so spreadsheet apps read it as text.
 *
 * ```typescript
 *
 *   import {toCsv} from '../../utils/csv';
 *   ...
 *
 *   const csv = toCsv(columns, rows);
 *
 * ```
 *
 * @param   columns The table's columns, in order.
 * @param   rows    The table's rows, keyed by column `key`.
 * @returns         The CSV text - a missing value is written empty.
 */
export const toCsv = (columns: CsvColumn[], rows: CsvRow[]): string => [
  toCsvLine(columns.map(function(column) {
    return column.label;
  })),
  ...rows.map(function(row) {
    return toCsvLine(columns.map(function(column) {
      return row[column.key] ?? '';
    }));
  })
].join('\r\n');

/**
 * Downloads CSV text as a file - UTF-8 with a byte order mark, so
 * spreadsheet apps read non-ASCII text correctly - releasing its object URL
 * once the download starts.
 *
 * ```typescript
 *
 *   import {downloadCsv, toCsv} from '../../utils/csv';
 *   ...
 *
 *   downloadCsv('fault-codes.csv', toCsv(columns, rows));
 *
 * ```
 *
 * @param fileName The downloaded file's name.
 * @param text     The CSV text.
 */
export const downloadCsv = (fileName: string, text: string) => {
  const blob = new Blob([`${UTF8_BOM}${text}`], {type: 'text/csv;charset=utf-8'});
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href     = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
